import { debounce } from "lodash";
import { observable, action, toJS } from "mobx";
import { CustomerIsTyping } from "./model/Outgoing";
import { loadHistory, sendWithSession } from "./Socket";

const syncTypingToServer = debounce(() => {
  sendWithSession(CustomerIsTyping());
}, 10000);

export const LocalStorage = {
  remove: (key) => {
    localStorage.removeItem(key);
  },
  set: (key, value, timeout = null) => {
    let member = {
      value,
      timeout,
      createdAt: new Date().getTime(),
    };
    localStorage.setItem(key, JSON.stringify(member));
  },
  get: (key) => {
    let raw = localStorage.getItem(key);
    if (raw) {
      try {
        let { value, timeout, createdAt } = JSON.parse(raw);
        if (!timeout || timeout < 0) return value;
        let now = new Date().getTime();
        if (now - createdAt > timeout) {
          localStorage.removeItem(key);
        } else return value;
      } catch (e) {
        console.log("Error on fetching value for", key, raw, e);
        localStorage.removeItem(key);
      }
    }
  },
};

export const CommonStore = observable({
  showViewChat: false,
  droppedFileList: [],
  messageList: [],
  isSessionActive: false,
  hasPendingMessage: false,
  chatRoomId: null,
  captchaAnswerResponse: null,
  firstMessage: null,
  isInitiatingRoom: false,
  lastTyping: null,
  allowCreateSession: true,
  disableCreateSessionReason: null,
  assignedAgent: null,
  sessionTimeoutCount: 0,
  customer: null,
  historyHasBeenLoaded: false,
  idpel: null,
  entryPointId: null,
  nextHistory: null,
  historyHasNext: true,
});

export const SetHistoryHasNext = action(function (bool) {
  console.log("Called", bool);
  CommonStore.historyHasNext = bool;
});

export const GetHistoryHasNext = action(function () {
  return toJS(CommonStore.historyHasNext);
});

export const GetNextHistory = action(function () {
  return toJS(CommonStore.nextHistory);
});

export const SetNextHistory = action(function (next) {
  CommonStore.nextHistory = next;
});

export const GetActiveEntryPointId = action(function () {
  return toJS(CommonStore.entryPointId);
});

export const SetActiveEntryPointId = action(function (entryPointId) {
  CommonStore.entryPointId = entryPointId;
});

export const GetIdPel = action(function () {
  return toJS(CommonStore.idpel);
});

export const SetIdPel = action(function (idpel) {
  CommonStore.idpel = idpel;
});

export const UnsetCustomer = action(function () {
  CommonStore.customer = null;
});

export const SetCustomer = action(function (customer) {
  console.log(customer);
  CommonStore.customer = customer;
});

export const GetCustomer = action(function () {
  console.log(CommonStore.customer);
  return toJS(CommonStore.customer);
});

export const IncrementSessionTimeoutCount = action(function () {
  CommonStore.sessionTimeoutCount = toJS(CommonStore.sessionTimeoutCount) + 1;
  console.log("After update", CommonStore.sessionTimeoutCount);
});

export const ResetSessionTimeoutCount = action(function () {
  CommonStore.sessionTimeoutCount = 0;
});

export const IsMaxTimeoutCountReached = action(function (max) {
  return toJS(CommonStore.sessionTimeoutCount) >= max;
});

export const SaveAssignedAgent = action(function (name) {
  CommonStore.assignedAgent = name;
});

export const ResetAssignedAgent = action(function () {
  CommonStore.assignedAgent = null;
});

export const IsAllowCreateSession = action(function () {
  return CommonStore.allowCreateSession;
});

export const DisableCreateSession = action(function (message) {
  CommonStore.allowCreateSession = false;
  CommonStore.disableCreateSessionReason = message;
});

export const EnableCreateSession = action(function () {
  CommonStore.allowCreateSession = true;
  CommonStore.disableCreateSessionReason = null;
});

export const IsSessionTimeout = action(function () {
  let maxDelta = 1000 * 60 * (CommonStore.sessionTimeoutCount + 1) * 5;
  let lastTyping = toJS(CommonStore.lastTyping);

  if (CommonStore.lastTyping) {
    return new Date().getTime() - lastTyping >= maxDelta;
  }
});

export const SetTypingTimestamp = action(function () {
  let lastTyping = new Date().getTime();
  CommonStore.lastTyping = lastTyping;
  LocalStorage.set("lastTyping", lastTyping);
  ResetSessionTimeoutCount();
  syncTypingToServer();
});

export const ClearTypingTimestamp = action(function () {
  CommonStore.lastTyping = null;
  LocalStorage.remove("lastTyping");
});

export const SendingInitRoom = action(function () {
  CommonStore.isInitiatingRoom = true;
});

export const ResetInitRoom = action(function () {
  CommonStore.isInitiatingRoom = false;
});

export const UpdateFirstMessage = action(function (str) {
  CommonStore.firstMessage = str;
});

export const GetFirstMessage = action(function () {
  return toJS(CommonStore.firstMessage);
});

export const UpdateCaptchaResponse = action(function (message) {
  CommonStore.captchaAnswerResponse = message;
});

export const UpdateMessageStore = action(function (message) {
  const store = CommonStore;
  store.message = message;
});

export const AddMessageToStore = action(function (message) {
  console.log(message, CommonStore.messageList);
  const list = toJS(CommonStore.messageList);
  const found = list.find((item) => item.messageId == message.messageId);
  if (!found) CommonStore.messageList.push(message);
  // console.log(CommonStore.messageList);
  // CommonStore.messageList[message.messageId] = message;
});

export const ResetMessageStore = action(function () {
  CommonStore.messageList = [];
});

export const AddDroppedFiles = action(function (fileList) {
  CommonStore.droppedFileList.replace(
    Array.from(fileList).map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    }))
  );
});

export const RemoveDroppedFiles = action(function () {
  CommonStore.droppedFileList = [];
});

export const HasDroppedFiles = () => {
  return toJS(CommonStore.droppedFileList).length > 0;
};

export const AgentCareStore = observable({
  displayPicture: "",
  status: "offline",
});

export const IsSessionActive = action(function () {
  return CommonStore.isSessionActive;
});

export const SetSessionIsActive = action(function () {
  CommonStore.isSessionActive = true;
});

export const SetSessionIsNotActive = action(function () {
  CommonStore.isSessionActive = false;
});

export const SetHasPendingMessage = action(function () {
  CommonStore.hasPendingMessage = true;
});

export const SetHasNoPendingMessage = action(function () {
  CommonStore.hasPendingMessage = false;
});

export const SetChatRoomId = action(function (id) {
  LocalStorage.set("chatRoomId", id);
  CommonStore.chatRoomId = id;
});

export const UnsetChatRoomId = action(function () {
  CommonStore.chatRoomId = null;
});

export const LoadHistoryAfterLoad = action(async function () {
  if (!CommonStore.historyHasBeenLoaded) {
    await loadHistory();
    CommonStore.historyHasBeenLoaded = true;
  }
});

export const MarkHistoryAsLoaded = action(function () {
  CommonStore.historyHasBeenLoaded = true;
});

export const IsChatRoomActive = () => !!CommonStore.chatRoomId;
